import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { Button, Card, Form, Input, Select, Space, Spin, Typography, message } from 'antd'
import * as api from '../lib/api'
import { useI18n } from '../i18n/I18nProvider'

const { Title, Text } = Typography

export default function DepartmentEditPage () {
  const { t } = useI18n()
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = !id || id === 'new'
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [organizations, setOrganizations] = useState([])
  const [regions, setRegions] = useState([])
  const [form] = Form.useForm()

  const loadDepartment = useCallback(async () => {
    if (isNew) return
    setLoading(true)
    try {
      const data = await api.getDepartment(id)
      form.setFieldsValue({
        name: data.name,
        code: data.code || '',
        organization: data.organization || null,
        region: data.region || null
      })
    } catch (e) {
      message.error(e.message || t('organization.messages.department_load_failed', 'Unable to load department'))
    } finally {
      setLoading(false)
    }
  }, [form, id, isNew, t])

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({ name: '', code: '', organization: null, region: null })
      setLoading(false)
    } else {
      loadDepartment()
    }
    Promise.all([
      api.getOrganizationOptions(),
      api.getRegionOptions()
    ]).then(([organizationOptions, regionOptions]) => {
      setOrganizations(organizationOptions)
      setRegions(regionOptions)
    }).catch(() => {
      setOrganizations([])
      setRegions([])
    })
  }, [form, id, isNew, loadDepartment])

  async function saveDepartment () {
    const values = await form.validateFields()
    values.business_unit = null
    setSaving(true)
    try {
      if (isNew) {
        await api.createDepartment(values)
        message.success(t('organization.messages.department_created', 'Department created'))
        navigate('/dashboard?tab=organization', { replace: true })
      } else {
        await api.updateDepartment(id, values)
        message.success(t('organization.messages.department_saved', 'Department saved'))
        navigate('/dashboard?tab=organization', { replace: true })
      }
    } catch (e) {
      message.error(e.message || t('organization.messages.department_save_failed', 'Unable to save department'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size='large' /></div>

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 20 }}>
      <Space direction='vertical' size='large' style={{ width: '100%' }}>
        <Link to='/dashboard?tab=organization'><Button icon={<ArrowLeftOutlined />}>{t('common.back_to_dashboard', 'Back to Dashboard')}</Button></Link>

        <div>
          <Title level={3} className='tti-page-title' style={{ marginBottom: 4 }}>{isNew ? t('organization.actions.create_department', 'Create Department') : t('organization.actions.edit_department', 'Edit Department')}</Title>
          <Text type='secondary'>{t('organization.departments.description', 'Manage departments within an organization and region.')}</Text>
        </div>

        <Card size='small' title={t('organization.departments.details', 'Department Details')}>
          <Form form={form} layout='vertical'>
            <Form.Item name='name' label={t('organization.fields.department_name', 'Department Name')} rules={[{ required: true, message: t('organization.validation.department_name_required', 'Department name is required') }]}>
              <Input placeholder={t('organization.examples.department_name', 'Finance')} />
            </Form.Item>
            <Form.Item name='code' label='Department Code (optional)'>
              <Input maxLength={80} placeholder='Enter the assigned department code' />
            </Form.Item>
            <Form.Item name='organization' label={t('organization.fields.organization', 'Organization')} rules={[{ required: true, message: t('organization.validation.organization_required', 'Organization is required') }]}>
              <Select
                options={organizations.map(organization => ({ value: organization.id, label: organization.name }))}
                placeholder={t('organization.fields.select_organization', 'Select organization')}
              />
            </Form.Item>
            <Form.Item name='region' label={t('organization.fields.region', 'Region')} rules={[{ required: true, message: t('organization.validation.region_required', 'Region is required') }]}>
              <Select
                options={regions.map(region => ({ value: region.id, label: region.name }))}
                placeholder={t('organization.fields.select_region', 'Select region')}
              />
            </Form.Item>
            <Button className='tti-btn-primary-grey' loading={saving} onClick={saveDepartment}>{t('organization.actions.save_department', 'Save Department')}</Button>
          </Form>
        </Card>
      </Space>
    </div>
  )
}
